// Locks an HTML/CSS surface to a fixed design width and scales it
// uniformly to fit its container. All internal proportions
// (buttons, gaps, knobs, labels) stay aligned to the real hardware
// the surface was authored against — `transform: scale` preserves
// every ratio, including text metrics.
//
// Tier 2 fidelity contract: docs/insights/tier-2-vector-fidelity.md.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

interface Props {
  /** Design-time width the children are authored for (in CSS px). */
  designWidth: number;
  className?: string;
  children: ReactNode;
}

export function ScaledSurface({ designWidth, className, children }: Props) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState<number | null>(null);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const update = () => {
      const w = outer.clientWidth;
      if (w > 0) setScale(Math.min(1, w / designWidth));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [designWidth]);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;
    const update = () => setInnerHeight(inner.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const outerStyle: CSSProperties = {
    width: "100%",
    // Reserve the scaled height so surrounding flow is correct —
    // transform: scale doesn't shrink layout box on its own.
    height: innerHeight !== null ? innerHeight * scale : undefined,
    overflow: "hidden",
    // Critical: when this div lives inside a flex container, flexbox's
    // default min-width:auto would force it to the unscaled inner width
    // (e.g. 1800px), breaking the scale calculation and causing overflow.
    minWidth: 0,
  };
  const innerStyle: CSSProperties = {
    width: designWidth,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
  };

  return (
    <div ref={outerRef} className={className} style={outerStyle}>
      <div ref={innerRef} style={innerStyle}>
        {children}
      </div>
    </div>
  );
}
